/**
 * Kalkyle optimistic-lock / concurrent-edit helpers.
 * API owns version enforcement (expected_updated_at / lock_version → 409);
 * FE must surface conflict and keep session deleted-row filtering.
 */

import {
  isPendingDeletedRow,
  pendingDeleteKeysFor,
  stripDeletedCalcItems,
} from "./offer-calc-row-sync";

export const CALCULATION_CONFLICT_TOKENS = new Set([
  "offer_calculation_changed_by_another_user",
  "offer_calculation_stale",
  "stale_object",
  "stale_calculation",
  "calculation_conflict",
]);

function collectErrorTokens(payload) {
  const tokens = [];
  const push = (value) => {
    if (typeof value === "string" && value.trim()) tokens.push(value.trim());
  };
  push(payload?.error);
  push(payload?.message);
  push(payload?.code);
  const errors = payload?.errors ?? payload?.response?.data?.errors;
  if (Array.isArray(errors)) {
    errors.forEach((entry) => {
      if (typeof entry === "string") push(entry);
      else if (entry && typeof entry === "object") {
        push(entry.message);
        push(entry.code);
        push(entry.error);
      }
    });
  } else if (typeof errors === "string") {
    push(errors);
  } else if (errors && typeof errors === "object") {
    Object.values(errors).forEach((value) => {
      if (Array.isArray(value)) value.forEach(push);
      else push(value);
    });
  }
  const nested = payload?.response?.data;
  if (nested && nested !== payload) {
    collectErrorTokens(nested).forEach((token) => tokens.push(token));
  }
  return tokens;
}

/**
 * True when the update envelope is a concurrent-edit conflict (HTTP 409 or
 * known stale/conflict token). Soft envelopes keep `status` from axios.
 */
export function isCalculationConflictError(payload) {
  if (!payload || typeof payload !== "object") return false;
  const status = payload.status ?? payload.response?.status;
  if (status === 409) return true;
  return collectErrorTokens(payload).some((token) =>
    CALCULATION_CONFLICT_TOKENS.has(token)
  );
}

/**
 * Fields the API can use for optimistic locking. Additive — ignore unknown
 * on older servers; API PR 379 owns enforcement.
 */
export function calculationOptimisticLockFields(calc) {
  if (!calc || typeof calc !== "object") return {};
  const fields = {};
  if (calc.updated_at != null && calc.updated_at !== "") {
    fields.expected_updated_at = calc.updated_at;
  }
  if (calc.lock_version != null && calc.lock_version !== "") {
    fields.lock_version = calc.lock_version;
  }
  if (calc.items_version != null && calc.items_version !== "") {
    fields.items_version = calc.items_version;
  }
  return fields;
}

/**
 * Copy optimistic-lock fields from a successful save onto a newer local calc
 * without overwriting local field edits / items.
 */
export function adoptSavedOptimisticLockFields(localCalc, savedCalc) {
  if (!localCalc) return localCalc;
  if (!savedCalc) return localCalc;
  const next = { ...localCalc };
  if (savedCalc.id != null && next.id == null) next.id = savedCalc.id;
  if (savedCalc.lock_version != null && savedCalc.lock_version !== "") {
    next.lock_version = savedCalc.lock_version;
  }
  if (savedCalc.updated_at != null && savedCalc.updated_at !== "") {
    next.updated_at = savedCalc.updated_at;
  }
  if (savedCalc.items_version != null && savedCalc.items_version !== "") {
    next.items_version = savedCalc.items_version;
  }
  return next;
}

/**
 * Stable client identity for a kalkyle line. Prefer explicit client_row_key,
 * then custom_fields, then draggableId. Never use row_order / description.
 */
export function itemClientRowKey(item) {
  if (!item || typeof item !== "object") return null;
  const fromField =
    item.client_row_key ||
    item.custom_fields?.client_row_key ||
    item.draggableId;
  if (fromField != null && String(fromField).trim() !== "") {
    return String(fromField);
  }
  return null;
}

let clientRowKeySeq = 0;
export function newClientRowKey() {
  clientRowKeySeq += 1;
  return `crk-${Date.now()}-${clientRowKeySeq}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

/** Ensure every non-deleted item has a stable client_row_key (+ draggableId). */
export function ensureClientRowKeys(calc) {
  if (!calc || typeof calc !== "object") return calc;
  const items = calc.items;
  if (!Array.isArray(items) || !items.length) return calc;
  let changed = false;
  const nextItems = items.map((item) => {
    if (!item || item.delete === true) return item;
    const existing = itemClientRowKey(item);
    if (existing) {
      const needsField = item.client_row_key !== existing;
      const needsDrag = !item.draggableId;
      const needsCf =
        item.custom_fields?.client_row_key !== existing;
      if (!needsField && !needsDrag && !needsCf) return item;
      changed = true;
      return {
        ...item,
        client_row_key: existing,
        draggableId: item.draggableId || existing,
        custom_fields: {
          ...(item.custom_fields || {}),
          client_row_key: existing,
        },
      };
    }
    changed = true;
    const key = newClientRowKey();
    return {
      ...item,
      client_row_key: key,
      draggableId: item.draggableId || key,
      custom_fields: {
        ...(item.custom_fields || {}),
        client_row_key: key,
      },
    };
  });
  return changed ? { ...calc, items: nextItems } : calc;
}

/**
 * Map client_row_key → saved item using rows actually sent in request A
 * (not current queued row_order). Existing ids match by id; creates zip to
 * newly appeared saved ids in sent-create order only.
 */
export function mapSentItemsToSavedByClientKey(sentItems, savedItems) {
  const map = new Map();
  const saved = savedItems || [];
  const sent = sentItems || [];
  const savedById = new Map();
  saved.forEach((item) => {
    if (item?.id != null && item.id !== "") {
      savedById.set(String(item.id), item);
    }
  });
  const priorIds = new Set(
    sent
      .filter((item) => item?.id != null && item.id !== "")
      .map((item) => String(item.id))
  );

  sent.forEach((sentItem) => {
    const key = itemClientRowKey(sentItem);
    if (!key) return;
    if (sentItem?.id != null && sentItem.id !== "") {
      const match = savedById.get(String(sentItem.id));
      if (match) map.set(key, match);
      return;
    }
    // Prefer echo of client_row_key from server custom_fields when present.
    const echoed = saved.find((s) => itemClientRowKey(s) === key);
    if (echoed?.id != null) {
      map.set(key, echoed);
    }
  });

  const creates = sent.filter(
    (item) =>
      item &&
      item.delete !== true &&
      (item.id == null || item.id === "") &&
      itemClientRowKey(item)
  );
  const unmatchedCreates = creates.filter(
    (item) => !map.has(itemClientRowKey(item))
  );
  const newSaved = saved.filter(
    (item) =>
      item?.id != null &&
      item.id !== "" &&
      !priorIds.has(String(item.id)) &&
      ![...map.values()].some((m) => String(m.id) === String(item.id))
  );
  unmatchedCreates.forEach((sentItem, index) => {
    const key = itemClientRowKey(sentItem);
    const match = newSaved[index];
    if (key && match) map.set(key, match);
  });

  return map;
}

/**
 * When a save returns server item ids while a newer local edit is queued,
 * stamp those ids onto matching id-less queued rows by stable client_row_key
 * correlated to rows sent in request A — never by row_order / index alone.
 */
export function reconcileQueuedItemsWithSaved(
  queuedCalc,
  savedCalc,
  { sentItems } = {}
) {
  if (!queuedCalc || !savedCalc) return queuedCalc;
  const queued = ensureClientRowKeys(queuedCalc);
  const savedItems = savedCalc.items || [];
  if (!savedItems.length && !(queued.items || []).length) {
    return adoptSavedOptimisticLockFields(queued, savedCalc);
  }

  const sent = sentItems || queuedCalc.items || [];
  const byClientKey = mapSentItemsToSavedByClientKey(sent, savedItems);
  const savedIds = new Set(
    savedItems
      .filter((item) => item?.id != null && item.id !== "")
      .flatMap((item) => [item.id, String(item.id)])
  );

  const nextItems = (queued.items || []).map((item) => {
    if (!item || item.delete === true) return item;
    const key = itemClientRowKey(item);
    const fromSent = key ? byClientKey.get(key) : null;

    if (
      item?.id != null &&
      !savedIds.has(item.id) &&
      !savedIds.has(String(item.id))
    ) {
      // Stale unknown DB id — do not create a duplicate line. Remap via
      // client key if we know the server id from save A; else drop the id.
      if (fromSent?.id != null) {
        return {
          ...item,
          id: fromSent.id,
          client_row_key: key,
          draggableId: item.draggableId || key || `id${fromSent.id}`,
          custom_fields: {
            ...(item.custom_fields || {}),
            client_row_key: key,
          },
        };
      }
      const { id: _staleId, ...rest } = item;
      return {
        ...rest,
        id: undefined,
        client_row_key: key || rest.client_row_key,
        draggableId: item.draggableId || key || newClientRowKey(),
      };
    }

    if (item?.id != null) return item;
    if (!fromSent?.id) return item;
    return {
      ...item,
      id: fromSent.id,
      client_row_key: key,
      draggableId: item.draggableId || key || `id${fromSent.id}`,
      custom_fields: {
        ...(item.custom_fields || {}),
        client_row_key: key,
      },
    };
  });

  return adoptSavedOptimisticLockFields(
    {
      ...queued,
      id: queued.id || savedCalc.id,
      items: nextItems,
    },
    savedCalc
  );
}

/**
 * Keep unsaved local field edits after conflict. Does NOT stamp server
 * lock_version onto the live draft — that would let autosave overwrite
 * the other tab. Use openCalculationConflict + explicit resolve instead.
 */
export function retainLocalCalcAfterConflict({
  localCalc,
  serverCalc,
  deletedKeys,
  stampServerLocks = false,
} = {}) {
  if (!localCalc && !serverCalc) return null;
  const local = localCalc
    ? stripDeletedCalcItems(ensureClientRowKeys(localCalc), deletedKeys)
    : null;
  if (!serverCalc) {
    return local
      ? { ...local, _conflict_retained: true, _conflict_unresolved: true }
      : null;
  }
  if (!local) {
    return {
      ...stripDeletedCalcItems(serverCalc, deletedKeys),
      _conflict_retained: true,
      _conflict_unresolved: true,
    };
  }
  const next = stampServerLocks
    ? adoptSavedOptimisticLockFields(local, serverCalc)
    : { ...local };
  return {
    ...next,
    _conflict_retained: true,
    _conflict_unresolved: true,
    _conflict_server: serverCalc,
  };
}

/**
 * True when a GET/reload body is a real OfferCalculation snapshot safe to adopt.
 * Soft failure envelopes often have only `status` / `message` (no `error`/`errors`)
 * — those must NOT become empty `items: []` via pristineServerCalcForConflict.
 */
export function isValidOfferCalculationSnapshot(payload) {
  if (!payload || typeof payload !== "object") return false;
  if (payload.error != null || payload.errors != null) return false;
  if (payload.id == null || payload.id === "") return false;
  return Array.isArray(payload.items);
}

/**
 * Pristine server snapshot for explicit «Bruk server».
 * Strips local-only conflict/delete markers — never session-delete filtering.
 * Returns null for non-snapshots (status/message-only bodies).
 */
export function pristineServerCalcForConflict(serverCalc) {
  if (!isValidOfferCalculationSnapshot(serverCalc)) return null;
  const {
    _deletedItems: _dropDeleted,
    _conflict_unresolved: _dropUnresolved,
    _conflict_server: _dropServer,
    _conflict_retained: _dropRetained,
    ...rest
  } = serverCalc;
  return {
    ...rest,
    items: rest.items.map((item) =>
      item && typeof item === "object" ? { ...item } : item
    ),
    nested_calculations: Array.isArray(rest.nested_calculations)
      ? rest.nested_calculations.map((child) =>
          child && typeof child === "object" ? { ...child } : child
        )
      : rest.nested_calculations,
  };
}

/**
 * Session delete keys that belong to this conflicted calculation.
 * Cleared only when the user explicitly chooses the server for that calc.
 */
export function sessionDeleteKeysForConflictCalc(
  deletedKeys,
  serverCalc,
  localCalc
) {
  const relevant = new Set();
  if (!deletedKeys || deletedKeys.size === 0) return relevant;

  const absorbMatchingKeys = (row) => {
    if (!row) return;
    const keys = pendingDeleteKeysFor(row);
    if (!keys.length) return;
    if (!keys.some((key) => deletedKeys.has(key)) && row.delete !== true) {
      if (!isPendingDeletedRow(deletedKeys, row)) return;
    }
    keys.forEach((key) => relevant.add(key));
  };

  // Server rows this tab had marked deleted must be restored on «Bruk server».
  (serverCalc?.items || []).forEach((row) => {
    if (!isPendingDeletedRow(deletedKeys, row)) return;
    pendingDeleteKeysFor(row).forEach((key) => relevant.add(key));
  });
  (localCalc?._deletedItems || []).forEach(absorbMatchingKeys);
  (serverCalc?._deletedItems || []).forEach(absorbMatchingKeys);

  return relevant;
}

/**
 * Remove this calculation's session delete keys from a Set (mutates + returns).
 * Leaves deletes that belong to other calculations untouched.
 *
 * Always recomputes from live `deletedKeys` + server/local rows (union any
 * snapshot on the conflict). A failed post-409 GET can leave
 * `sessionDeleteKeysToDiscard` empty even while `deletedRowKeysRef` still
 * holds this-calc deletes — trusting only that empty Set would strip restored
 * lines on the next persist after «Bruk server».
 */
export function discardSessionDeletesForConflictCalc(deletedKeys, conflict) {
  if (!deletedKeys || typeof deletedKeys.delete !== "function") {
    return deletedKeys;
  }
  const toDiscard = sessionDeleteKeysForConflictCalc(
    deletedKeys,
    conflict?.server,
    conflict?.local
  );
  if (conflict?.sessionDeleteKeysToDiscard instanceof Set) {
    conflict.sessionDeleteKeysToDiscard.forEach((key) => toDiscard.add(key));
  }
  toDiscard.forEach((key) => deletedKeys.delete(key));
  return deletedKeys;
}

/**
 * Explicit unresolved conflict: local draft + pristine server snapshot.
 * Auto-writes must stop until the user resolves.
 *
 * Server is stored WITHOUT stripDeletedCalcItems — «Bruk server» must restore
 * lines the other tab kept even if this tab had deleted them locally.
 */
export function openCalculationConflict({
  calculationKey,
  localCalc,
  serverCalc,
  deletedKeys,
} = {}) {
  const local = retainLocalCalcAfterConflict({
    localCalc,
    serverCalc,
    deletedKeys,
    stampServerLocks: false,
  });
  const server = pristineServerCalcForConflict(serverCalc);
  return {
    calculationKey: calculationKey || null,
    unresolved: true,
    local,
    server,
    sessionDeleteKeysToDiscard: sessionDeleteKeysForConflictCalc(
      deletedKeys,
      serverCalc,
      localCalc
    ),
  };
}

export function isCalculationConflictUnresolved(conflict) {
  return Boolean(conflict?.unresolved);
}

/** «Bruk mine»: keep local items; adopt server lock fields for an intentional save. */
export function resolveConflictUseLocal(conflict) {
  if (!conflict?.local) return null;
  const stamped = adoptSavedOptimisticLockFields(
    conflict.local,
    conflict.server || {}
  );
  const { _conflict_unresolved, _conflict_server, _conflict_retained, ...rest } =
    stamped;
  return ensureClientRowKeys(rest);
}

/**
 * «Bruk server»: pristine server snapshot for this calc only.
 * Does NOT apply session delete filtering — that would drop the other user's
 * retained lines. Call discardSessionDeletesForConflictCalc separately.
 *
 * @param {object} conflict
 * @param {Set} [_deletedKeys] ignored (kept for call-site compatibility)
 */
export function resolveConflictUseServer(conflict, _deletedKeys) {
  if (!conflict?.server) return null;
  return pristineServerCalcForConflict(conflict.server);
}

/**
 * Soft-sync / non-explicit adopt: keep session deleted-row filtering so a
 * normal reload cannot resurrect rows the user deleted on this tab.
 * Explicit «Bruk server» must use resolveConflictUseServer instead.
 */
export function applyServerCalcAfterConflict(serverCalc, deletedKeys) {
  if (!serverCalc) return null;
  return stripDeletedCalcItems(serverCalc, deletedKeys);
}

/**
 * Pure resolution used by the Kalkyle UI (and component/queue tests).
 * Discards local deletes for the conflicted calculation only, clears queued
 * writes for that key, and returns the pristine server calc to show.
 */
export function applyConflictUseServerResolution({
  conflict,
  deletedKeys,
  pendingSaveByKey,
  lockFieldsByKey,
} = {}) {
  if (!conflict?.unresolved) {
    return {
      resolved: false,
      server: null,
      deletedKeys,
      pendingSaveByKey,
      lockFieldsByKey,
      cancelQueuedWrites: false,
    };
  }
  const server = resolveConflictUseServer(conflict);
  if (!server) {
    return {
      resolved: false,
      server: null,
      deletedKeys,
      pendingSaveByKey,
      lockFieldsByKey,
      cancelQueuedWrites: false,
      // Caller must keep the conflict UI open — never dismiss empty snapshot.
      keepConflictOpen: true,
    };
  }

  const nextDeletes =
    deletedKeys instanceof Set ? new Set(deletedKeys) : new Set();
  discardSessionDeletesForConflictCalc(nextDeletes, {
    ...conflict,
    server: conflict.server || server,
  });

  const key = conflict.calculationKey;
  const nextPending =
    pendingSaveByKey instanceof Map
      ? new Map(pendingSaveByKey)
      : new Map();
  const nextLocks =
    lockFieldsByKey instanceof Map
      ? new Map(lockFieldsByKey)
      : new Map();
  if (key) {
    nextPending.delete(key);
    nextLocks.set(key, adoptSavedOptimisticLockFields({}, server));
  }

  return {
    resolved: true,
    server,
    deletedKeys: nextDeletes,
    pendingSaveByKey: nextPending,
    lockFieldsByKey: nextLocks,
    calculationKey: key || null,
    cancelQueuedWrites: true,
    saveState: "saved",
  };
}

/**
 * When post-409 GET failed, optionally merge a reloaded server snapshot before
 * «Bruk server». Never recommends dismissing / adopting without a real calc
 * snapshot (id + items array). Pass live `deletedKeys` so retry_adopt can
 * recompute this-calc session deletes against the reloaded server rows.
 *
 * @returns {{
 *   action: "noop"|"adopt"|"keep_open"|"retry_adopt",
 *   conflict: object|null,
 *   dismissConflict: boolean,
 * }}
 */
export function planConflictUseServerAdoption({
  conflict,
  reloadedServer = null,
  deletedKeys = null,
} = {}) {
  if (!conflict?.unresolved) {
    return { action: "noop", conflict: conflict || null, dismissConflict: false };
  }
  if (isValidOfferCalculationSnapshot(conflict.server)) {
    return { action: "adopt", conflict, dismissConflict: false };
  }
  if (isValidOfferCalculationSnapshot(reloadedServer)) {
    const keysForReopen = new Set();
    if (deletedKeys instanceof Set) {
      deletedKeys.forEach((key) => keysForReopen.add(key));
    }
    if (conflict.sessionDeleteKeysToDiscard instanceof Set) {
      conflict.sessionDeleteKeysToDiscard.forEach((key) =>
        keysForReopen.add(key)
      );
    }
    const next = openCalculationConflict({
      calculationKey: conflict.calculationKey,
      localCalc: conflict.local,
      serverCalc: reloadedServer,
      deletedKeys: keysForReopen,
    });
    if (!isValidOfferCalculationSnapshot(next.server)) {
      return { action: "keep_open", conflict, dismissConflict: false };
    }
    return { action: "retry_adopt", conflict: next, dismissConflict: false };
  }
  return { action: "keep_open", conflict, dismissConflict: false };
}

/**
 * Trailing-write persist loop used by the Kalkyle tab (and regression tests).
 * While persist(A) is in flight, a newer pending B is reconciled with A's
 * response (ids + lock_version) before B is sent.
 *
 * @param {{
 *   getPending: () => object|null|undefined,
 *   setPending: (calc: object|null) => void,
 *   persist: (calc: object) => Promise<object>,
 *   onConflict?: (args: { sent: object, error: object, pending: object|null }) =>
 *     'break'|'continue',
 * }} opts
 */
export async function runTrailingCalculationPersistQueue({
  getPending,
  setPending,
  persist,
  onConflict,
} = {}) {
  let lastSaved = null;
  while (getPending?.()) {
    const nextCalc = getPending();
    setPending?.(null);
    let saved;
    try {
      saved = await persist(nextCalc);
    } catch (error) {
      if (
        onConflict &&
        isCalculationConflictError(error?.payload || error)
      ) {
        const action = await onConflict({
          sent: nextCalc,
          error: error?.payload || error,
          pending: getPending?.() || null,
        });
        if (action === "break") break;
        continue;
      }
      throw error;
    }
    if (saved && isCalculationConflictError(saved)) {
      const action = await onConflict?.({
        sent: nextCalc,
        error: saved,
        pending: getPending?.() || null,
      });
      if (action === "break" || !onConflict) break;
      continue;
    }
    lastSaved = saved;
    const pending = getPending?.();
    if (pending && saved) {
      setPending?.(
        reconcileQueuedItemsWithSaved(pending, saved, {
          sentItems: nextCalc?.items,
        })
      );
    }
  }
  return lastSaved;
}
